import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../context/NotificationContext";
import { auth, db } from "../firebase";
import { signOut, updateEmail, sendEmailVerification } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";

function initialFromName(name, fallback = "-") {
  if (!name || typeof name !== "string") return fallback;
  return name.trim().charAt(0).toUpperCase() || fallback;
}

function SettingsPage() {
  const navigate = useNavigate();
  const { notificationCount } = useNotifications();
  const user = auth.currentUser;
  const [profile, setProfile] = useState({
    displayName: "",
    email: "",
    phone: "",
    clinic: "",
    license: "",
    title: "",
    specializations: [],
    education: "",
    graduationYear: "",
    experience: "",
    perSessionFee: "",
    defaultPackage: "",
  });
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneOTP, setPhoneOTP] = useState("");
  const [phoneOTPSent, setPhoneOTPSent] = useState(false);
  const [phoneOTPVerified, setPhoneOTPVerified] = useState(false);
  const [phoneOTPError, setPhoneOTPError] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailOTP, setEmailOTP] = useState("");
  const [emailOTPSent, setEmailOTPSent] = useState(false);
  const [emailOTPVerified, setEmailOTPVerified] = useState(false);
  const [emailOTPError, setEmailOTPError] = useState("");
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
  });
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrSaved, setAddrSaved] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingSpecialization, setEditingSpecialization] = useState(false);
  const [specializationTab, setSpecializationTab] = useState("default"); // "default" or "custom"
  const [customSpecialization, setCustomSpecialization] = useState("");
  const [selectedFromList, setSelectedFromList] = useState([]);
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingForm, setBillingForm] = useState({
    perSessionFee: "",
  });
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingSaved, setBillingSaved] = useState(false);
  const [packages, setPackages] = useState([]);
  const [editingPackages, setEditingPackages] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: "",
    appointmentCount: "",
    lumpSumValue: "",
  });
  const [editingPackageIndex, setEditingPackageIndex] = useState(null);
  const [packageSaving, setPackageSaving] = useState(false);

  // Default list of physiotherapy specializations
  const defaultSpecializations = [
    "Orthopedic Physiotherapy",
    "Neurological Physiotherapy",
    "Cardiopulmonary Physiotherapy",
    "Sports Physiotherapy",
    "Pediatric Physiotherapy",
    "Geriatric Physiotherapy",
    "Women's Health Physiotherapy",
    "Manual Therapy",
    "Vestibular Rehabilitation",
    "Aquatic Physiotherapy",
    "Oncology Rehabilitation",
    "Pain Management",
    "Post-Surgical Rehabilitation",
    "Musculoskeletal Physiotherapy",
    "Electrotherapy",
    "Occupational Therapy",
    "Ergonomics",
    "Home Care Physiotherapy",
  ];

  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfile({
            displayName: user.displayName || data.displayName || "",
            email: user.email || data.email || "",
            phone: data.phone || "",
            clinic: data.clinic || "",
            license: data.license || "",
            title: data.title || "Physiotherapist",
            specializations: data.specializations || [],
            education: data.education || "",
            graduationYear: data.graduationYear || "",
            experience: data.experience || "",
            perSessionFee: data.perSessionFee || "",
          });
          setPackages(data.packages || []);
          setAddress({
            line1: data.address?.line1 || "",
            line2: data.address?.line2 || "",
            city: data.address?.city || "",
            state: data.address?.state || "",
            country: data.address?.country || "",
            pincode: data.address?.pincode || "",
          });
        } else {
          setProfile((p) => ({
            ...p,
            displayName: user.displayName || "",
            email: user.email || "",
          }));
        }
      } catch (e) {
        console.warn("Failed to load profile", e);
      }
    };
    loadProfile();
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (e) {
      console.error("Logout failed", e);
    }
  };

  // Generate a 6-digit OTP
  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Send OTP to phone (stored in Firestore temporarily)
  const sendPhoneOTP = async () => {
    if (!user || !phoneInput.trim()) {
      setPhoneOTPError("Please enter a valid phone number");
      return;
    }

    const phoneRegex = /^\+?[\d\s\-()]{10,}$/;
    if (!phoneRegex.test(phoneInput.trim())) {
      setPhoneOTPError("Please enter a valid phone number");
      return;
    }

    setPhoneOTPError("");
    setSaving(true);
    try {
      const otp = generateOTP();
      const otpRef = collection(db, "otps");
      await addDoc(otpRef, {
        userId: user.uid,
        type: "phone",
        value: phoneInput.trim(),
        otp: otp,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes
      });
      setPhoneOTPSent(true);
      // In production, send OTP via SMS service (Twilio, etc.)
      // For now, we'll show it in console/alert for testing
      alert(
        `OTP sent to ${phoneInput.trim()}. OTP: ${otp} (This is for testing only)`,
      );
    } catch (e) {
      console.error("Failed to send phone OTP", e);
      setPhoneOTPError("Failed to send OTP. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Verify phone OTP
  const verifyPhoneOTP = async () => {
    if (!user || !phoneOTP.trim()) {
      setPhoneOTPError("Please enter the OTP");
      return;
    }

    setPhoneOTPError("");
    setSaving(true);
    try {
      const otpRef = collection(db, "otps");
      const q = query(
        otpRef,
        where("userId", "==", user.uid),
        where("type", "==", "phone"),
        where("value", "==", phoneInput.trim()),
      );
      const snapshot = await getDocs(q);

      let found = false;
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const now = new Date();
        const expiresAt = data.expiresAt?.toDate
          ? data.expiresAt.toDate()
          : new Date(data.expiresAt);

        if (data.otp === phoneOTP.trim() && expiresAt > now) {
          found = true;
          // Delete used OTP
          deleteDoc(doc(db, "otps", docSnap.id));
        }
      });

      if (!found) {
        setPhoneOTPError("Invalid or expired OTP. Please try again.");
        return;
      }

      setPhoneOTPVerified(true);
      // Save phone number
      const docRef = doc(db, "users", user.uid);
      await setDoc(docRef, { phone: phoneInput.trim() }, { merge: true });
      setProfile((p) => ({ ...p, phone: phoneInput.trim() }));
      setEditingPhone(false);
      setPhoneOTPSent(false);
      setPhoneOTPVerified(false);
      setPhoneOTP("");
      setPhoneInput("");
    } catch (e) {
      console.error("Failed to verify phone OTP", e);
      setPhoneOTPError("Failed to verify OTP. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Send OTP to email
  const sendEmailOTP = async () => {
    if (!user || !emailInput.trim()) {
      setEmailOTPError("Please enter a valid email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput.trim())) {
      setEmailOTPError("Please enter a valid email address");
      return;
    }

    if (emailInput.trim() === user.email) {
      setEmailOTPError("This is already your current email");
      return;
    }

    setEmailOTPError("");
    setSaving(true);
    try {
      const otp = generateOTP();
      const otpRef = collection(db, "otps");
      await addDoc(otpRef, {
        userId: user.uid,
        type: "email",
        value: emailInput.trim(),
        otp: otp,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes
      });
      setEmailOTPSent(true);
      // In production, send OTP via email service
      // For now, we'll show it in console/alert for testing
      alert(
        `OTP sent to ${emailInput.trim()}. OTP: ${otp} (This is for testing only)`,
      );
    } catch (e) {
      console.error("Failed to send email OTP", e);
      setEmailOTPError("Failed to send OTP. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Verify email OTP and update email
  const verifyEmailOTP = async () => {
    if (!user || !emailOTP.trim()) {
      setEmailOTPError("Please enter the OTP");
      return;
    }

    setEmailOTPError("");
    setSaving(true);
    try {
      const otpRef = collection(db, "otps");
      const q = query(
        otpRef,
        where("userId", "==", user.uid),
        where("type", "==", "email"),
        where("value", "==", emailInput.trim()),
      );
      const snapshot = await getDocs(q);

      let found = false;
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const now = new Date();
        const expiresAt = data.expiresAt?.toDate
          ? data.expiresAt.toDate()
          : new Date(data.expiresAt);

        if (data.otp === emailOTP.trim() && expiresAt > now) {
          found = true;
          // Delete used OTP
          deleteDoc(doc(db, "otps", docSnap.id));
        }
      });

      if (!found) {
        setEmailOTPError("Invalid or expired OTP. Please try again.");
        return;
      }

      setEmailOTPVerified(true);
      // Update email in Firebase Auth
      await updateEmail(user, emailInput.trim());
      await sendEmailVerification(user);

      // Update email in Firestore
      const docRef = doc(db, "users", user.uid);
      await setDoc(docRef, { email: emailInput.trim() }, { merge: true });
      setProfile((p) => ({ ...p, email: emailInput.trim() }));
      setEditingEmail(false);
      setEmailOTPSent(false);
      setEmailOTPVerified(false);
      setEmailOTP("");
      setEmailInput("");
    } catch (e) {
      console.error("Failed to verify email OTP", e);
      if (e.code === "auth/requires-recent-login") {
        setEmailOTPError(
          "Please log out and log back in to change your email.",
        );
      } else {
        setEmailOTPError("Failed to verify OTP. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && text) {
      navigator.clipboard.writeText(text);
    }
  };

  const saveAddress = async () => {
    if (!user) return;
    // basic validation
    const trimmed = {
      line1: address.line1.trim(),
      line2: address.line2.trim(),
      city: address.city.trim(),
      state: address.state.trim(),
      country: address.country.trim(),
      pincode: address.pincode.trim(),
    };
    const pinOk = /^\d{4,10}$/.test(trimmed.pincode);
    if (
      !trimmed.line1 ||
      !trimmed.city ||
      !trimmed.state ||
      !trimmed.country ||
      !pinOk
    ) {
      // soft fail: highlight by brief saved=false indicator; we won't alert()
      setAddrSaved(false);
      return;
    }
    setAddrSaving(true);
    setAddrSaved(false);
    try {
      const docRef = doc(db, "users", user.uid);
      await setDoc(docRef, { address: trimmed }, { merge: true });
      setAddress(trimmed);
      setAddrSaved(true);
    } catch (e) {
      console.error("Failed to save address", e);
      setAddrSaved(false);
    } finally {
      setAddrSaving(false);
      // hide saved tick after a moment
      setTimeout(() => setAddrSaved(false), 1800);
    }
  };

  const handleAddSpecializations = async () => {
    if (!user) return;
    let newSpecs = [...profile.specializations];

    if (specializationTab === "default" && selectedFromList.length > 0) {
      // Add selected from default list
      selectedFromList.forEach((spec) => {
        if (!newSpecs.includes(spec)) {
          newSpecs.push(spec);
        }
      });
    } else if (specializationTab === "custom" && customSpecialization.trim()) {
      // Add custom specialization
      const custom = customSpecialization.trim();
      if (!newSpecs.includes(custom)) {
        newSpecs.push(custom);
      }
    }

    try {
      const docRef = doc(db, "users", user.uid);
      await setDoc(docRef, { specializations: newSpecs }, { merge: true });
      setProfile((p) => ({ ...p, specializations: newSpecs }));
      setEditingSpecialization(false);
      setCustomSpecialization("");
      setSelectedFromList([]);
    } catch (e) {
      console.error("Failed to save specializations", e);
    }
  };

  const handleRemoveSpecialization = async (specToRemove) => {
    if (!user) return;
    const newSpecs = profile.specializations.filter((s) => s !== specToRemove);
    try {
      const docRef = doc(db, "users", user.uid);
      await setDoc(docRef, { specializations: newSpecs }, { merge: true });
      setProfile((p) => ({ ...p, specializations: newSpecs }));
    } catch (e) {
      console.error("Failed to remove specialization", e);
    }
  };

  const toggleSpecializationFromList = (spec) => {
    if (selectedFromList.includes(spec)) {
      setSelectedFromList(selectedFromList.filter((s) => s !== spec));
    } else {
      setSelectedFromList([...selectedFromList, spec]);
    }
  };

  const saveBilling = async () => {
    if (!user) return;
    setBillingSaving(true);
    setBillingSaved(false);
    try {
      const docRef = doc(db, "users", user.uid);
      const updateData = {};
      if (billingForm.perSessionFee.trim()) {
        updateData.perSessionFee = parseFloat(billingForm.perSessionFee) || 0;
      }
      await setDoc(docRef, updateData, { merge: true });
      setProfile((p) => ({
        ...p,
        perSessionFee: billingForm.perSessionFee,
      }));
      setBillingSaved(true);
      setTimeout(() => {
        setEditingBilling(false);
        setBillingSaved(false);
      }, 1500);
    } catch (e) {
      console.error("Failed to save billing info", e);
      setBillingSaved(false);
    } finally {
      setBillingSaving(false);
    }
  };

  const savePackage = async () => {
    if (!user) return;
    if (
      !packageForm.name.trim() ||
      !packageForm.appointmentCount ||
      parseInt(packageForm.appointmentCount) <= 0 ||
      !packageForm.lumpSumValue ||
      parseFloat(packageForm.lumpSumValue) <= 0
    ) {
      return;
    }
    setPackageSaving(true);
    try {
      const newPackage = {
        name: packageForm.name.trim(),
        appointmentCount: parseInt(packageForm.appointmentCount),
        lumpSumValue: parseFloat(packageForm.lumpSumValue),
      };
      let updatedPackages;
      if (editingPackageIndex !== null) {
        updatedPackages = [...packages];
        updatedPackages[editingPackageIndex] = newPackage;
      } else {
        updatedPackages = [...packages, newPackage];
      }
      const docRef = doc(db, "users", user.uid);
      await setDoc(docRef, { packages: updatedPackages }, { merge: true });
      setPackages(updatedPackages);
      setPackageForm({ name: "", appointmentCount: "", lumpSumValue: "" });
      setEditingPackageIndex(null);
      setEditingPackages(false);
    } catch (e) {
      console.error("Failed to save package", e);
    } finally {
      setPackageSaving(false);
    }
  };

  const deletePackage = async (index) => {
    if (!user) return;
    try {
      const updatedPackages = packages.filter((_, i) => i !== index);
      const docRef = doc(db, "users", user.uid);
      await setDoc(docRef, { packages: updatedPackages }, { merge: true });
      setPackages(updatedPackages);
    } catch (e) {
      console.error("Failed to delete package", e);
    }
  };

  const editPackage = (index) => {
    const pkg = packages[index];
    setPackageForm({
      name: pkg.name,
      appointmentCount: pkg.appointmentCount.toString(),
      lumpSumValue: pkg.lumpSumValue ? pkg.lumpSumValue.toString() : "",
    });
    setEditingPackageIndex(index);
    setEditingPackages(true);
  };

  return (
    <div className="settings-layout">
      <header
        style={{
          paddingTop: "34px",
          paddingBottom: "16px",
          paddingLeft: "40px",
          paddingRight: "24px",
          display: "flex",
          flexDirection: "column",
          background: "white",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "600" }}>
          My Profile
        </h1>
        <div
          style={{
            height: "1px",
            background: "#e2e8f0",
            marginTop: "16px",
            marginLeft: "0px",
            marginRight: "0px",
            marginBottom: "0",
            width: "calc(100% - 0px)",
            marginLeft: "0px",
          }}
        />
      </header>

      <div className="settings-content-wrapper" style={{ paddingTop: "72px" }}>
        <main className="settings-main-content">
          <section className="settings-section">
            <h3 className="settings-section-title">Contact Information</h3>
            <div className="settings-card-group">
              <div
                className="settings-contact-row"
                onClick={() => {
                  setPhoneInput(profile.phone || "");
                  setEditingPhone(true);
                }}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-box">
                    <span className="material-symbols-outlined">call</span>
                  </div>
                  <div className="settings-row-text">
                    <p className="settings-label">Mobile</p>
                    <p className="settings-value">
                      {profile.phone || "Add phone number"}
                    </p>
                  </div>
                </div>
                <button
                  className="settings-copy-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(profile.phone);
                  }}
                  aria-label="Copy phone"
                >
                  <span className="material-symbols-outlined">
                    content_copy
                  </span>
                </button>
              </div>

              <div
                className="settings-contact-row"
                onClick={() => {
                  setEmailInput(profile.email || "");
                  setEditingEmail(true);
                  setEmailOTPSent(false);
                  setEmailOTPVerified(false);
                  setEmailOTP("");
                  setEmailOTPError("");
                }}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-box">
                    <span className="material-symbols-outlined">mail</span>
                  </div>
                  <div className="settings-row-text">
                    <p className="settings-label">Email</p>
                    <p className="settings-value">
                      {profile.email || "Add email address"}
                    </p>
                  </div>
                </div>
                <button
                  className="settings-copy-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(profile.email);
                  }}
                  aria-label="Copy email"
                >
                  <span className="material-symbols-outlined">
                    content_copy
                  </span>
                </button>
              </div>

              <div
                className="settings-contact-row no-border"
                onClick={() => {
                  setEditingAddress(true);
                }}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-box">
                    <span className="material-symbols-outlined">
                      location_on
                    </span>
                  </div>
                  <div className="settings-row-text">
                    <p className="settings-label">Clinic Address</p>
                    <p className="settings-value">
                      {address?.line1
                        ? `${address.line1}${
                            address.city ? ", " + address.city : ""
                          }${address.state ? ", " + address.state : ""}${
                            address.pincode ? " - " + address.pincode : ""
                          }`
                        : "Add clinic address"}
                    </p>
                  </div>
                </div>
                <button className="settings-arrow-btn" aria-label="Edit clinic">
                  <span className="material-symbols-outlined">
                    arrow_forward_ios
                  </span>
                </button>
              </div>
            </div>
          </section>

          <section className="settings-section">
            <h3 className="settings-section-title">Professional Details</h3>
            <div className="settings-prof-card">
              <div className="settings-specializations">
                <p className="settings-label">Specializations</p>
                <div className="settings-chips">
                  {profile.specializations.length > 0 ? (
                    profile.specializations.map((s, i) => (
                      <span key={i} className="settings-chip">
                        {s}
                        <button
                          className="settings-chip-remove"
                          onClick={() => handleRemoveSpecialization(s)}
                          aria-label="Remove specialization"
                        >
                          <span className="material-symbols-outlined">
                            close
                          </span>
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="settings-chip-empty">
                      No specializations added
                    </span>
                  )}
                  <button
                    className="settings-chip-add"
                    onClick={() => {
                      setEditingSpecialization(true);
                      setSpecializationTab("default");
                      setSelectedFromList([]);
                      setCustomSpecialization("");
                    }}
                  >
                    <span className="material-symbols-outlined">add</span> Add
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="settings-section">
            <h3 className="settings-section-title">Billing & Packages</h3>
            <div className="settings-card-group">
              <div
                className="settings-contact-row"
                onClick={() => {
                  setBillingForm({
                    perSessionFee: profile.perSessionFee || "",
                  });
                  setEditingBilling(true);
                }}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-box">
                    <span className="material-symbols-outlined">payments</span>
                  </div>
                  <div className="settings-row-text">
                    <p className="settings-label">Per Session Fee</p>
                    <p className="settings-value">
                      {profile.perSessionFee
                        ? `₹${parseFloat(profile.perSessionFee).toFixed(2)}`
                        : "Not set"}
                    </p>
                  </div>
                </div>
                <button
                  className="settings-arrow-btn"
                  aria-label="Edit billing"
                >
                  <span className="material-symbols-outlined">
                    arrow_forward_ios
                  </span>
                </button>
              </div>

              <div
                className="settings-contact-row no-border"
                onClick={() => {
                  setPackageForm({ name: "", appointmentCount: "" });
                  setEditingPackageIndex(null);
                  setEditingPackages(true);
                }}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-box">
                    <span className="material-symbols-outlined">
                      inventory_2
                    </span>
                  </div>
                  <div className="settings-row-text">
                    <p className="settings-label">Treatment Packages</p>
                    <p className="settings-value">
                      {packages.length > 0
                        ? `${packages.length} package${
                            packages.length !== 1 ? "s" : ""
                          } configured`
                        : "No packages configured"}
                    </p>
                  </div>
                </div>
                <button
                  className="settings-arrow-btn"
                  aria-label="Manage packages"
                >
                  <span className="material-symbols-outlined">
                    arrow_forward_ios
                  </span>
                </button>
              </div>
            </div>
          </section>

          <section className="settings-section">
            <h3 className="settings-section-title">Account</h3>
            <div className="settings-card-group">
              <button
                className="settings-account-btn"
                onClick={() => navigate("/settings/notifications")}
              >
                <span>Notifications</span>
                <span className="material-symbols-outlined">
                  arrow_forward_ios
                </span>
              </button>
              <button
                className="settings-account-btn"
                onClick={() => navigate("/settings/privacy")}
              >
                <span>Privacy &amp; Security</span>
                <span className="material-symbols-outlined">
                  arrow_forward_ios
                </span>
              </button>
              <button
                className="settings-account-btn logout"
                onClick={handleLogout}
              >
                <span>Log Out</span>
                <span className="material-symbols-outlined">logout</span>
              </button>
            </div>
          </section>
        </main>
      </div>

      {editingAddress && (
        <div
          className="settings-modal"
          onClick={() => setEditingAddress(false)}
        >
          <div
            className="settings-modal-card settings-form"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Edit Clinic Address</h3>
            <div className="settings-field">
              <label>Address Line 1</label>
              <input
                type="text"
                value={address.line1}
                onChange={(e) =>
                  setAddress((a) => ({ ...a, line1: e.target.value }))
                }
                placeholder="Street, building, area"
                autoFocus
              />
            </div>
            <div className="settings-field">
              <label>Address Line 2 (Optional)</label>
              <input
                type="text"
                value={address.line2}
                onChange={(e) =>
                  setAddress((a) => ({ ...a, line2: e.target.value }))
                }
                placeholder="Apartment, floor, landmark"
              />
            </div>
            <div className="settings-field-row">
              <div className="settings-field">
                <label>City</label>
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) =>
                    setAddress((a) => ({ ...a, city: e.target.value }))
                  }
                  placeholder="City"
                />
              </div>
              <div className="settings-field">
                <label>State</label>
                <input
                  type="text"
                  value={address.state}
                  onChange={(e) =>
                    setAddress((a) => ({ ...a, state: e.target.value }))
                  }
                  placeholder="State"
                />
              </div>
            </div>
            <div className="settings-field-row">
              <div className="settings-field">
                <label>Country</label>
                <input
                  type="text"
                  value={address.country}
                  onChange={(e) =>
                    setAddress((a) => ({ ...a, country: e.target.value }))
                  }
                  placeholder="Country"
                />
              </div>
              <div className="settings-field">
                <label>Pincode</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={address.pincode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setAddress((a) => ({ ...a, pincode: val }));
                  }}
                  placeholder="e.g., 560001"
                />
              </div>
            </div>
            <div className="settings-modal-actions">
              <button onClick={() => setEditingAddress(false)}>Cancel</button>
              <button onClick={saveAddress} disabled={addrSaving}>
                {addrSaving ? "Saving..." : addrSaved ? "Saved" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPhone && (
        <div
          className="settings-modal"
          onClick={() => {
            setEditingPhone(false);
            setPhoneOTPSent(false);
            setPhoneOTPVerified(false);
            setPhoneOTP("");
            setPhoneOTPError("");
          }}
        >
          <div
            className="settings-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Edit Phone Number</h3>
            {!phoneOTPSent ? (
              <>
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => {
                    setPhoneInput(e.target.value);
                    setPhoneOTPError("");
                  }}
                  placeholder="+1 (555) 012-3456"
                  autoFocus
                  disabled={saving}
                />
                {phoneOTPError && (
                  <p className="settings-otp-error">{phoneOTPError}</p>
                )}
                <div className="settings-modal-actions">
                  <button
                    onClick={() => {
                      setEditingPhone(false);
                      setPhoneOTPSent(false);
                      setPhoneOTPVerified(false);
                      setPhoneOTP("");
                      setPhoneOTPError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendPhoneOTP}
                    disabled={saving || !phoneInput.trim()}
                  >
                    {saving ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="settings-otp-info">
                  Enter the OTP sent to {phoneInput}
                </p>
                <input
                  type="text"
                  value={phoneOTP}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setPhoneOTP(val);
                    setPhoneOTPError("");
                  }}
                  placeholder="Enter 6-digit OTP"
                  autoFocus
                  disabled={saving || phoneOTPVerified}
                  maxLength={6}
                />
                {phoneOTPError && (
                  <p className="settings-otp-error">{phoneOTPError}</p>
                )}
                <div className="settings-modal-actions">
                  <button
                    onClick={() => {
                      setPhoneOTPSent(false);
                      setPhoneOTP("");
                      setPhoneOTPError("");
                    }}
                    disabled={saving || phoneOTPVerified}
                  >
                    Change Number
                  </button>
                  <button
                    onClick={verifyPhoneOTP}
                    disabled={
                      saving || phoneOTPVerified || phoneOTP.length !== 6
                    }
                  >
                    {saving
                      ? "Verifying..."
                      : phoneOTPVerified
                        ? "Verified"
                        : "Verify OTP"}
                  </button>
                </div>
                <button
                  className="settings-resend-otp"
                  onClick={sendPhoneOTP}
                  disabled={saving}
                >
                  Resend OTP
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {editingEmail && (
        <div
          className="settings-modal"
          onClick={() => {
            setEditingEmail(false);
            setEmailOTPSent(false);
            setEmailOTPVerified(false);
            setEmailOTP("");
            setEmailOTPError("");
          }}
        >
          <div
            className="settings-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Edit Email Address</h3>
            {!emailOTPSent ? (
              <>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailOTPError("");
                  }}
                  placeholder="example@email.com"
                  autoFocus
                  disabled={saving}
                />
                {emailOTPError && (
                  <p className="settings-otp-error">{emailOTPError}</p>
                )}
                <div className="settings-modal-actions">
                  <button
                    onClick={() => {
                      setEditingEmail(false);
                      setEmailOTPSent(false);
                      setEmailOTPVerified(false);
                      setEmailOTP("");
                      setEmailOTPError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendEmailOTP}
                    disabled={saving || !emailInput.trim()}
                  >
                    {saving ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="settings-otp-info">
                  Enter the OTP sent to {emailInput}
                </p>
                <input
                  type="text"
                  value={emailOTP}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setEmailOTP(val);
                    setEmailOTPError("");
                  }}
                  placeholder="Enter 6-digit OTP"
                  autoFocus
                  disabled={saving || emailOTPVerified}
                  maxLength={6}
                />
                {emailOTPError && (
                  <p className="settings-otp-error">{emailOTPError}</p>
                )}
                <div className="settings-modal-actions">
                  <button
                    onClick={() => {
                      setEmailOTPSent(false);
                      setEmailOTP("");
                      setEmailOTPError("");
                    }}
                    disabled={saving || emailOTPVerified}
                  >
                    Change Email
                  </button>
                  <button
                    onClick={verifyEmailOTP}
                    disabled={
                      saving || emailOTPVerified || emailOTP.length !== 6
                    }
                  >
                    {saving
                      ? "Verifying..."
                      : emailOTPVerified
                        ? "Verified"
                        : "Verify OTP"}
                  </button>
                </div>
                <button
                  className="settings-resend-otp"
                  onClick={sendEmailOTP}
                  disabled={saving}
                >
                  Resend OTP
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {editingSpecialization && (
        <div
          className="settings-modal"
          onClick={() => setEditingSpecialization(false)}
        >
          <div
            className="settings-modal-card settings-spec-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Add Specialization</h3>

            <div className="settings-spec-tabs">
              <button
                className={`settings-spec-tab ${
                  specializationTab === "default" ? "active" : ""
                }`}
                onClick={() => setSpecializationTab("default")}
              >
                Select from List
              </button>
              <button
                className={`settings-spec-tab ${
                  specializationTab === "custom" ? "active" : ""
                }`}
                onClick={() => setSpecializationTab("custom")}
              >
                Add Custom
              </button>
            </div>

            {specializationTab === "default" ? (
              <div className="settings-spec-list">
                {defaultSpecializations
                  .filter((spec) => !profile.specializations.includes(spec))
                  .map((spec, i) => (
                    <label key={i} className="settings-spec-item">
                      <input
                        type="checkbox"
                        checked={selectedFromList.includes(spec)}
                        onChange={() => toggleSpecializationFromList(spec)}
                      />
                      <span>{spec}</span>
                    </label>
                  ))}
                {defaultSpecializations.filter(
                  (spec) => !profile.specializations.includes(spec),
                ).length === 0 && (
                  <p className="settings-spec-empty">
                    All default specializations have been added
                  </p>
                )}
              </div>
            ) : (
              <div className="settings-spec-custom">
                <label>Enter Custom Specialization</label>
                <input
                  type="text"
                  value={customSpecialization}
                  onChange={(e) => setCustomSpecialization(e.target.value)}
                  placeholder="e.g., Sports Injury Rehabilitation"
                  autoFocus
                />
              </div>
            )}

            <div className="settings-modal-actions">
              <button onClick={() => setEditingSpecialization(false)}>
                Cancel
              </button>
              <button
                onClick={handleAddSpecializations}
                disabled={
                  (specializationTab === "default" &&
                    selectedFromList.length === 0) ||
                  (specializationTab === "custom" &&
                    !customSpecialization.trim())
                }
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {editingBilling && (
        <div
          className="settings-modal"
          onClick={() => {
            setEditingBilling(false);
            setBillingSaved(false);
          }}
        >
          <div
            className="settings-modal-card settings-form"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Edit Per Session Fee</h3>
            <div className="settings-field">
              <label>Per Session Fee (INR)</label>
              <input
                type="number"
                value={billingForm.perSessionFee}
                onChange={(e) =>
                  setBillingForm((b) => ({
                    ...b,
                    perSessionFee: e.target.value,
                  }))
                }
                placeholder="e.g., 50.00"
                min="0"
                step="0.01"
                autoFocus
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  marginTop: "4px",
                }}
              >
                This will be used as the default fee for new patients
              </p>
            </div>
            <div className="settings-modal-actions">
              <button
                onClick={() => {
                  setEditingBilling(false);
                  setBillingSaved(false);
                }}
              >
                Cancel
              </button>
              <button onClick={saveBilling} disabled={billingSaving}>
                {billingSaving ? "Saving..." : billingSaved ? "Saved" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPackages && (
        <div
          className="settings-modal"
          onClick={() => {
            setEditingPackages(false);
            setPackageForm({
              name: "",
              appointmentCount: "",
              lumpSumValue: "",
            });
            setEditingPackageIndex(null);
          }}
        >
          <div
            className="settings-modal-card settings-form"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px", maxHeight: "80vh", overflowY: "auto" }}
          >
            <h3>
              {editingPackageIndex !== null ? "Edit Package" : "Add Package"}
            </h3>
            <div className="settings-field">
              <label>Package Name</label>
              <input
                type="text"
                value={packageForm.name}
                onChange={(e) =>
                  setPackageForm((p) => ({
                    ...p,
                    name: e.target.value,
                  }))
                }
                placeholder="e.g., Standard Package, Premium Package"
                autoFocus
              />
            </div>
            <div className="settings-field">
              <label>Number of Appointments</label>
              <input
                type="number"
                value={packageForm.appointmentCount}
                onChange={(e) =>
                  setPackageForm((p) => ({
                    ...p,
                    appointmentCount: e.target.value,
                  }))
                }
                placeholder="e.g., 10"
                min="1"
                step="1"
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  marginTop: "4px",
                }}
              >
                Number of appointments included in this package
              </p>
            </div>
            <div className="settings-field">
              <label>Package Amount (INR)</label>
              <input
                type="number"
                value={packageForm.lumpSumValue}
                onChange={(e) =>
                  setPackageForm((p) => ({
                    ...p,
                    lumpSumValue: e.target.value,
                  }))
                }
                placeholder="e.g., 5000.00"
                min="0"
                step="0.01"
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  marginTop: "4px",
                }}
              >
                Total amount for this package. Per session fee will be
                calculated automatically.
                {packageForm.appointmentCount &&
                  packageForm.lumpSumValue &&
                  parseFloat(packageForm.lumpSumValue) > 0 && (
                    <span
                      style={{
                        display: "block",
                        marginTop: "4px",
                        fontWeight: "600",
                        color: "var(--primary)",
                      }}
                    >
                      Per session: ₹
                      {(
                        parseFloat(packageForm.lumpSumValue) /
                        parseInt(packageForm.appointmentCount)
                      ).toFixed(2)}
                    </span>
                  )}
              </p>
            </div>
            <div className="settings-modal-actions">
              <button
                onClick={() => {
                  setEditingPackages(false);
                  setPackageForm({
                    name: "",
                    appointmentCount: "",
                    lumpSumValue: "",
                  });
                  setEditingPackageIndex(null);
                }}
              >
                Cancel
              </button>
              <button
                onClick={savePackage}
                disabled={
                  packageSaving ||
                  !packageForm.name.trim() ||
                  !packageForm.appointmentCount ||
                  parseInt(packageForm.appointmentCount) <= 0 ||
                  !packageForm.lumpSumValue ||
                  parseFloat(packageForm.lumpSumValue) <= 0
                }
              >
                {packageSaving
                  ? "Saving..."
                  : editingPackageIndex !== null
                    ? "Update"
                    : "Add"}
              </button>
            </div>

            {packages.length > 0 && (
              <div
                style={{
                  marginTop: "24px",
                  borderTop: "1px solid var(--border-color)",
                  paddingTop: "16px",
                }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "var(--text-primary)",
                  }}
                >
                  Existing Packages
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {packages.map((pkg, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px",
                        background: "var(--bg-input)",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: "600",
                            fontSize: "14px",
                            color: "var(--text-primary)",
                          }}
                        >
                          {pkg.name}
                        </p>
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: "12px",
                            color: "var(--text-muted)",
                          }}
                        >
                          {pkg.appointmentCount} appointment
                          {pkg.appointmentCount !== 1 ? "s" : ""}
                        </p>
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: "12px",
                            color: "var(--text-primary)",
                            fontWeight: "600",
                          }}
                        >
                          ₹
                          {pkg.lumpSumValue
                            ? parseFloat(pkg.lumpSumValue).toFixed(2)
                            : "0.00"}{" "}
                          total
                          {pkg.lumpSumValue && pkg.appointmentCount && (
                            <span
                              style={{
                                color: "var(--text-muted)",
                                fontWeight: "500",
                                marginLeft: "8px",
                              }}
                            >
                              (₹
                              {(
                                parseFloat(pkg.lumpSumValue) /
                                parseInt(pkg.appointmentCount)
                              ).toFixed(2)}{" "}
                              per session)
                            </span>
                          )}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => editPackage(index)}
                          style={{
                            padding: "6px 12px",
                            background: "var(--primary)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deletePackage(index)}
                          style={{
                            padding: "6px 12px",
                            background: "transparent",
                            color: "var(--error)",
                            border: "1px solid var(--error)",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <nav className="appts-bottom-nav">
        <button className="appts-nav-item" onClick={() => navigate("/doctor")}>
          <span className="material-symbols-outlined">home</span>
          <span>Home</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/appointments")}
        >
          <span className="material-symbols-outlined">calendar_month</span>
          <span>Appointments</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/patients")}
        >
          <span className="material-symbols-outlined">groups</span>
          <span>Patients</span>
        </button>
        <button
          className="appts-nav-item active"
          onClick={() => navigate("/settings")}
        >
          <span className="material-symbols-outlined fill-current">person</span>
          <span>Profile</span>
        </button>
      </nav>
    </div>
  );
}

export default SettingsPage;
